import React, { useState, useEffect } from "react";
import { Table } from "ant-table-extensions";
import { Spin, message, Tag, Row, Col, Card, Button, Tabs, Modal, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import PageBreadcrumb from "../component/PageBreadcrumb";
import { GetAllWorkshops } from "../redux/actions/WorkshopAction";
import AddWorkshop from "../component/AddWorkshop";
import WorkshopDetails from "../component/WorkshopDetails";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const { Text } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);

const Workshops = () => {
  const dispatch = useDispatch();
  const { workshops, pagination } = useSelector((state) => state.WorkshopReducer);
  
  const [loader, setLoader] = useState(false);
  const [workshopData, setWorkshopData] = useState([]);
  const [activeTab, setActiveTab] = useState("1");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWorkshopId, setSelectedWorkshopId] = useState(null);
  const [workshopDetailsVisible, setWorkshopDetailsVisible] = useState(false);

  useEffect(() => {
    getWorkshops(1, true);
  }, []);

  useEffect(() => {
    if (workshops && Array.isArray(workshops) && workshops.length > 0) {
      const formattedData = workshops.map((workshop, index) => ({
        key: workshop._id || workshop.id,
        no: ((currentPage - 1) * 20) + index + 1,
        title: workshop.title,
        type: workshop.type,
        description: workshop.description || "",
        bannerImageUrl: workshop.bannerImageUrl || "",
        meetingUrl: workshop.meetingUrl || "",
        workshopDate: workshop.workshopDate,
        startTime: workshop.startTime,
        endTime: workshop.endTime,
        workshopDuration: workshop.workshopDuration,
        status: workshop.isActive ? "active" : "inactive",
        createdAt: workshop.createdAt,
        updatedAt: workshop.updatedAt,
        _id: workshop._id || workshop.id,
      }));
      setWorkshopData(formattedData);
    } else {
      setWorkshopData([]);
    }
  }, [workshops, currentPage]);

  const getWorkshops = (activePage, reset) => {
    setLoader(true);
    if (reset) {
      setCurrentPage(1);
    } else {
      setCurrentPage(activePage);
    }
    
    const data = {
      page: activePage,
      limit: 20,
    };

    const onSuccess = (data) => {
      setLoader(false);
    };

    const onError = (error) => {
      setLoader(false);
      message.error(error, 8);
    };

    dispatch(GetAllWorkshops(data, onSuccess, onError));
  };

  const columns = [
    { title: "No", width: 60, dataIndex: "no", key: "1" },
    { 
      title: "Title", 
      width: 250, 
      dataIndex: "title", 
      key: "2",
      render: (title, record) => (
        <Text 
          style={{ 
            color: "#1890ff", 
            cursor: "pointer",
            textDecoration: "underline"
          }}
          onClick={() => {
            setSelectedWorkshopId(record._id);
            setWorkshopDetailsVisible(true);
          }}
        >
          {title}
        </Text>
      )
    },
    { title: "Type", width: 120, dataIndex: "type", key: "3" },
    {
      title: "Status",
      width: 100,
      dataIndex: "status",
      key: "4",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "red"}>
          {status === "active" ? "ACTIVE" : "INACTIVE"}
        </Tag>
      ),
    },
    { 
      title: "Date", 
      width: 120, 
      dataIndex: "workshopDate", 
      key: "5",
      render: (date) => {
        if (date) {
          return dayjs(date).format("DD/MM/YYYY");
        }
        return "-";
      }
    },
    {
      title: "Start Time",
      width: 100,
      dataIndex: "startTime",
      key: "6",
      render: (time, record) => {
        if (!time) return "-";
        // Combine UTC date and time, convert to IST, show HH:mm
        return dayjs.utc(`${record.workshopDate.split('T')[0]}T${time}`)
          .tz("Asia/Kolkata")
          .format("HH:mm");
      }
    },
    {
      title: "End Time",
      width: 100,
      dataIndex: "endTime",
      key: "7",
      render: (time, record) => {
        if (!time) return "-";
        return dayjs.utc(`${record.workshopDate.split('T')[0]}T${time}`)
          .tz("Asia/Kolkata")
          .format("HH:mm");
      }
    },
    { title: "Duration", width: 100, dataIndex: "workshopDuration", key: "8" },
    {
      title: "Action",
      key: "edit",
      width: 80,
      fixed: "right",
      render: (data) => (
        <AddWorkshop 
          name="Edit" 
          value={data} 
          getWorkshops={getWorkshops}
          currentPage={currentPage}
        />
      ),
    },
  ];



  const renderWorkshopTable = () => (
    <div style={{ marginTop: 20 }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <h3>Workshop Management</h3>
              <AddWorkshop name="Add Workshop" getWorkshops={getWorkshops} />
            </div>
            
            <Table
              columns={columns}
              dataSource={workshopData}
              scroll={{ x: 1200 }}
              loading={loader}
              pagination={{
                current: currentPage,
                total: pagination?.totalItems || workshopData.length,
                pageSize: pagination?.limit || 20,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                onChange: (page) => getWorkshops(page, false),
              }}
            />
          </Card>
        </Col>
      </Row>

      <WorkshopDetails
        workshopId={selectedWorkshopId}
        visible={workshopDetailsVisible}
        onClose={() => {
          setWorkshopDetailsVisible(false);
          setSelectedWorkshopId(null);
        }}
      />
    </div>
  );

  return (
    <div>
      <PageBreadcrumb route={["Workshops"]} />
      
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        {
          key: "1",
          label: "All Workshops",
          children: renderWorkshopTable()
        }
      ]} />
    </div>
  );
};

export default Workshops; 